/**
 * AuthDataContext - Centralized user data fetching
 * 
 * This context fetches user data ONCE and shares it with:
 * - PermissionContext (for permissions, role, isSuperAdmin)
 * - OrgContext (for organisation_id, organisation_name)
 * 
 * OPTIMIZATION: Reduces 3+ API calls to just 1 on login
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import BASE_URL from "../config";

const AuthDataContext = createContext();

export const AuthDataProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserData = useCallback(async (user) => {
    if (!user) {
      setUserData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await user.getIdToken();

      // Single API call that returns user info + permissions
      const response = await axios.get(`${BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ AuthDataContext: User data loaded with", response.data.permissions?.length || 0, "permissions");
      setUserData(response.data);
    } catch (err) {
      console.error("❌ AuthDataContext: Error fetching user data:", err);
      setError(err);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      fetchUserData(user);
    });

    return () => unsubscribe();
  }, [fetchUserData]);

  // Refresh user data (useful after role changes, org switches, etc.)
  const refreshUserData = useCallback(async () => {
    const user = getAuth().currentUser;
    if (user) {
      await fetchUserData(user);
    }
  }, [fetchUserData]);

  return (
    <AuthDataContext.Provider value={{ userData, loading, error, refreshUserData }}>
      {children}
    </AuthDataContext.Provider>
  );
};

export const useAuthData = () => useContext(AuthDataContext);
